import { inject, Injectable, isSignal, Signal, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Dialog, type DialogRef } from '@angular/cdk/dialog';
import { AlertContainer, AlertDialogInteraction, AlertButtonConfig, type AlertDialogInput } from './alert-container/alert-container';
import { getAlertIconSVGCode } from './alerts.helper';
import { Translate } from '../core/translate';
import { openDialogFn } from '@helpers';

/* Servicio para mostrar alertas */

export type AlertType = 'info' | 'warning' | 'success' | 'error';

@Injectable({
  providedIn: 'root',
})
export class Alerts {
  // Dependencias
  private readonly t = inject(Translate).translateFn();
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly openDialog = openDialogFn(inject(Dialog));

  // Signals / atributos
  dialogRef: DialogRef | null = null;
  autoCloseTimer: number | null = null;

  // Cierra la alerta actual
  close() {
    if (this.autoCloseTimer !== null)
      clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = null;
    this.dialogRef?.close();
  }

  // Muestra la alerta indicada y devuelve la promesa de cierre con el resultado de la interacción
  show(
    alertType: AlertType,
    config?: {
      title?: string | Signal<string> | null,
      msg?: string | Signal<string> | null,
      easyClose?: boolean,
      autoClose?: boolean,
      interaction?: AlertDialogInteraction,
      cancelButton?: AlertButtonConfig,
      acceptButton?: AlertButtonConfig,
    },
  ): Promise<boolean | null> {
    // Parámetros de configuración
    const $icon = signal(this.domSanitizer.bypassSecurityTrustHtml(getAlertIconSVGCode(alertType)));
    let $title = isSignal(config?.title) ? config.title : signal(config?.title ?? null);
    const $message = isSignal(config?.msg) ? config.msg : signal(config?.msg ?? null);
    const $interaction = signal<AlertDialogInteraction>(config?.interaction ?? 'accept');
    const $cancelButton = signal<AlertButtonConfig | null>(config?.cancelButton ?? null);
    const $acceptButton = signal<AlertButtonConfig | null>(config?.acceptButton ?? null);

    if (config === undefined)
      config = {};

    if (config.title === undefined)
      $title = this.t(alertType);

    // Establecer valores predefinidos para cada tipo de alerta
    switch (alertType) {
      case 'info':
        if (config.easyClose === undefined)
          config.easyClose = true;

        if (config.autoClose === undefined)
          config.autoClose = true;
        break;

      case 'success':
        if (config.easyClose === undefined)
          config.easyClose = true;

        if (config.autoClose === undefined)
          config.autoClose = true;

        break;

      case 'warning':
        if (config.easyClose === undefined)
          config.easyClose = true;

        if (config.autoClose === undefined)
          config.autoClose = false;

        if (config.interaction === undefined)
          $interaction.set('cancel_accept');
        break;

      case 'error':
        if (config.easyClose === undefined)
          config.easyClose = false;

        if (config.autoClose === undefined)
          config.autoClose = false;
    }

    // Verificar si debe cancelarse un timer de auto cierre activo
    if (this.autoCloseTimer !== null) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    // Si ya había un diálogo abierto, cerrarlo antes de abrir uno nuevo
    if (this.dialogRef !== null) {
      this.dialogRef.close();
      this.dialogRef = null;
    }

    // Abrir diálogo de alerta y establecer referencia
    const inputData: AlertDialogInput = { $icon, $title, $message, $interaction, $cancelButton, $acceptButton };

    const { dialogRef, closed } = this.openDialog<AlertDialogInput, boolean | null>(AlertContainer, { data: inputData, backdropClass: 'cdk-alert', panelClass: 'cdk-alert-panel', easyClose: config.easyClose });
    this.dialogRef = dialogRef;

    // Establecer timer de auto cierre
    if (config.autoClose)
      this.autoCloseTimer = setTimeout(() => {
        this.dialogRef?.close();
        this.autoCloseTimer = null;
      }, 6000);

    // Abrir diálogo de alerta y devolver promesa de evento de cierre
    return closed;
  }
}
